import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Heart, Truck, Users, ShieldCheck, PackageCheck } from 'lucide-react';
import SEO from '../components/SEO';

const VALUES = [
  { icon: Heart, title: 'Rooted in Community', desc: 'With roots in the Caribbean wholesale tradition, we understand multicultural retail. We bring that authenticity to every product we carry for every community we serve.' },
  { icon: Users, title: 'Family Serving Families', desc: "We're a family business built to serve family businesses. We understand the hustle of running a shop and we're here to make restocking the easy part." },
  { icon: Truck, title: 'Reliable & Canada-Wide', desc: 'Based in the Greater Toronto Area with shipping across Canada. Your orders packed with care and delivered on time, every time.' },
  { icon: ShieldCheck, title: 'Honest Wholesale Pricing', desc: 'No middleman markup, no hidden fees. Straightforward bulk pricing designed so your margins make sense and your customers keep coming back.' },
  { icon: PackageCheck, title: 'Curated for Diversity', desc: 'We only carry what we believe in — authentic multicultural goods your customers actually want. From tools to natural products, every item is handpicked.' },
  { icon: MapPin, title: 'Rooted in the GTA', desc: 'Proudly based in the Greater Toronto Area, serving the ethnic and multicultural business community across Ontario and beyond.' },
];

const AboutUs: React.FC = () => {
  return (
    <div className="bg-brand-warm">
      <SEO
        title="About Us"
        description="The Yaad Shop — a Caribbean-rooted wholesale supplier serving multicultural stores across Canada. Family serving families, community serving community."
        path="/about"
      />
      {/* Page Header */}
      <section className="bg-brand-navy">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-10 py-14">
          <div className="text-xs font-bold uppercase tracking-[2px] text-brand-teal-light mb-3">Our Story</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-white mb-3">About The Yaad Shop</h1>
          <p className="text-base text-white/60 max-w-[560px] leading-relaxed">
            Multicultural wholesale, built by people who know the communities.
          </p>
        </div>
      </section>

      {/* Heritage Story */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-10 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text */}
          <div>
            <div className="text-xs font-bold uppercase tracking-[2px] text-brand-green mb-4">Our Heritage</div>
            <h2 className="font-display text-3xl font-extrabold text-brand-navy mb-6">
              From Community Roots to Canada-Wide Wholesale
            </h2>
            <div className="space-y-4 text-[15px] text-brand-text-muted leading-relaxed">
              <p>
                The Yaad Shop was born from a simple truth: ethnic and multicultural businesses in Canada deserve a wholesale supplier that actually understands what they need.
              </p>
              <p>
                With roots in the Caribbean wholesale tradition, we grew up in the shops, behind the counters, stacking shelves and serving customers. That experience taught us what multicultural stores across Canada need — and now we deliver it.
              </p>
              <p>
                Based in the Greater Toronto Area, we serve African, Caribbean, Asian, and Latin American stores across Canada. From tools and flags to incense and natural products — we carry the goods your customers are looking for, at prices that make your business work.
              </p>
              <p>
                This isn't a side project. This is what we do. <span className="text-brand-navy font-semibold">Family serving families, community serving community.</span>
              </p>
            </div>
          </div>

          {/* Heritage illustration */}
          <div className="hidden lg:block">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg border border-gray-100">
              <img
                src="/images/sections/heritage-photo.png"
                alt="Caribbean wholesale heritage — a vibrant market storefront with tropical palms, colorful products, and welcoming community"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="bg-brand-navy">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-10 py-16 text-center">
          <div className="max-w-[700px] mx-auto">
            <div className="text-xs font-bold uppercase tracking-[2px] text-brand-teal-light mb-4">Our Mission</div>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-white mb-5 leading-snug">
              "To be the wholesale partner that every ethnic business in Canada can trust — with authentic products, honest prices, and the personal service of a family operation."
            </h2>
            <div className="flex items-center justify-center gap-3 text-white/50 text-sm">
              <span className="text-lg">🌍</span>
              <span>The Yaad Shop</span>
              <span className="text-white/20">|</span>
              <span>Greater Toronto Area, Canada</span>
              <span className="text-lg">🇨🇦</span>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-10 py-20">
        <div className="text-center mb-12">
          <div className="text-xs font-bold uppercase tracking-[2px] text-brand-green mb-3">What We Stand For</div>
          <h2 className="font-display text-4xl font-extrabold text-brand-navy mb-3">Our Values</h2>
          <p className="text-base text-brand-text-muted max-w-[520px] mx-auto leading-relaxed">
            Everything we do comes back to these principles.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {VALUES.map((item) => (
            <div
              key={item.title}
              className="bg-white rounded-2xl p-8 border border-gray-100 transition-all hover:shadow-lg hover:-translate-y-1 hover:border-brand-teal"
            >
              <div className="w-[50px] h-[50px] rounded-xl bg-gradient-to-br from-brand-green/10 to-brand-green/5 flex items-center justify-center mb-5">
                <item.icon className="w-6 h-6 text-brand-green" />
              </div>
              <h3 className="font-display font-bold text-lg text-brand-navy mb-2.5">{item.title}</h3>
              <p className="text-sm text-brand-text-muted leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* By the Numbers */}
      <section className="bg-brand-light-bg">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-10 py-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: '200+', label: 'Products Available' },
              { value: 'GTA', label: 'Based & Ships Canada-wide' },
              { value: '10', label: 'Product Categories' },
              { value: '100%', label: 'Canadian Owned' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="font-display font-extrabold text-[36px] text-brand-teal leading-none mb-2">{stat.value}</div>
                <div className="text-sm text-brand-text-muted">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-10 py-20">
        <div className="relative bg-gradient-to-br from-brand-navy to-brand-navy-light rounded-3xl px-10 py-16 text-center overflow-hidden">
          <div
            className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(200,16,46,0.15),transparent_60%),radial-gradient(circle_at_70%_50%,rgba(255,184,0,0.12),transparent_50%)]"
            aria-hidden="true"
          />
          <div className="relative z-10">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white mb-3.5">
              Ready to Partner With Us?
            </h2>
            <p className="text-[17px] text-white/65 max-w-[500px] mx-auto leading-relaxed mb-8">
              Apply for a wholesale account and join the growing number of Canadian businesses we proudly supply.
            </p>
            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <Link
                to="/apply"
                className="bg-brand-teal text-white px-8 py-3.5 font-semibold text-[15px] rounded-[10px] hover:bg-brand-teal-dark transition-all shadow-lg shadow-brand-teal/25 hover:-translate-y-0.5"
              >
                Apply for Wholesale Account
              </Link>
              <a
                href="mailto:info@theyaadshop.com"
                className="bg-white/10 text-white border border-white/20 px-8 py-3.5 font-semibold text-[15px] rounded-[10px] hover:bg-white/20 transition-colors inline-block"
              >
                Get in Touch
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
